import { MouseEvent, useMemo } from "react";
import { useAppDispatch } from "hooks/hooks";
import { useSelector } from "react-redux";
import { FormatDateShort } from "utils/dateUtils";
import CancelIcon from "@mui/icons-material/Cancel";
import { grey } from "@mui/material/colors";
import {
  selectState,
  setSelectedIndex,
  setDeleteInputModalOpen,
  updateInput,
  setUpdateInputItemPopover,
} from "../input.slice";
import { categoryDictionary } from "utils/translationDictionary";
import { FormatNumber, FormatNumberWithUnitOrNull } from "utils/numberUtils";
import { IInput } from "Dashboard/scenes/Consumables/domain/IInput";
import InfoTooltip from "components/Popup/InfoTooltip";
import { IConsumablesInputUpdate } from "Dashboard/scenes/Consumables/domain/IConsumablesInputUpdate";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import { IOption } from "Dashboard/domain/IOption";
import PartnerSimpleSelect from "../../../../Partners/components/PartnerSimpleSelect";
import { styles } from "Dashboard/components/SelectStyle";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { hasFullAccessTo, hasWriteAccessTo } from "utils/authUtil";
import { CONSUMABLES_MODULE_NAME } from "Dashboard/scenes/Consumables/domain/ConsumablesModule";

interface IProps {
  input: IInput;
  partnerOptions: IOption[];
  index: number;
  lastTableElementRef?: any;
};

const ConsumablesInput = ({ input, partnerOptions, index, lastTableElementRef }: IProps) => {

  const dispatch = useAppDispatch();
  const { selectedIndex } = useSelector(selectState);
  const { user } = useSelector(selectStateAuth);
  const pricePerUnitNotSet = input.pricePerUnit === null || input.pricePerUnit === undefined || input.pricePerUnit === 0;

  const setSelected = (index: number) => (e: MouseEvent<HTMLDivElement>) => {
    dispatch(setSelectedIndex(index));
    e.preventDefault();
  };

  const onChangeBasicField = (data: IConsumablesInputUpdate) => {
    updateBasicFieldWithDelay(data);
  };

  const updateBasicFieldWithDelay = useMemo(
    () => debounce(async (data: IConsumablesInputUpdate) => {
      try {
        await dispatch(updateInput(data)).unwrap();
        toast.success("Izmena je uspešno zabeležena.");
      } catch (status: any) {
        if (status === 403) {
          toast.error("Neočekivana greška");
        } else if (status === 400) {
          toast.error("Neočekivana greška");
        }
      }
    }, 1000),
    [dispatch]
  );

  const onChangePartner = async (newPartnerId: number) => {
    await onChangeBasicField({ inputId: input.inputId, supplierPartnerId: newPartnerId });
  };

  return (
    <>
      <div
        ref={lastTableElementRef}
        key={index} onClick={setSelected(index)}
        className={`row row_content top-row-content
                  ${index === selectedIndex ? "active" : ""}`}
      >
        <div className="col">
          <div className="bg-item bg-transparent center">
            <span className="xxxCheck">
              <input type="checkbox" checked={input.xxx} readOnly />
            </span>
          </div>
        </div>
        <div className="col center" onClick={hasWriteAccessTo(user, CONSUMABLES_MODULE_NAME)
          ? (e) => {
            dispatch(
              setUpdateInputItemPopover({
                isOpen: true,
                left: e.clientX,
                top: e.clientY,
                itemRowIndex: index,
                focus: "itemCode",
              })
            )
          }
          : undefined}>
          <div className={`${hasWriteAccessTo(user, CONSUMABLES_MODULE_NAME)
            ? "hoverBorder" : ""} bg-item bg-item-low-importance 
            overflowMinWidth light center`}>
            <InfoTooltip tooltip={input.itemCode}
              classesProp="bg-text-overflow">
              {input.itemCode}
            </InfoTooltip>
          </div>
        </div>

        <div className="col center">
          <InfoTooltip tooltip={`${input.billOfLandingId}`}
            classesProp="bg-item bg-item-low-importance overflowMinWidth light">
            {hasWriteAccessTo(user, CONSUMABLES_MODULE_NAME)
              ? (
                <input className="bg-item-low-importance bg-text-overflow light center"
                  defaultValue={input.billOfLandingId} type="text"
                  onChange={(e) => {
                    onChangeBasicField({ inputId: input.inputId, billOfLandingId: e.target.value })
                  }}
                />
              )
              : (<span className="bg-text-overflow center">{input.billOfLandingId}</span>)}
          </InfoTooltip>
        </div>
        <div className="col strong left">
          <div className="bg-item overflowMinWidth upper">
            <InfoTooltip tooltip={input.item.name}
              classesProp={`bg-text-overflow 
              ${Math.round(input.quantityAmountRemaining * 1000) === 0
                  ? " light" : ""}`}
            >
              {input.item.name}
            </InfoTooltip>
          </div>
        </div>
        <div className="col">
          <div className="bg-item bg-item-low-importance">
            {hasWriteAccessTo(user, CONSUMABLES_MODULE_NAME)
              ? (
                <PartnerSimpleSelect
                  customStyles={styles.dropdownInTable}
                  defaultValue={input.supplier.partnerId}
                  options={partnerOptions}
                  onChange={onChangePartner} />
              )
              : input.supplier.shortName}
          </div>
        </div>
        <div className="col center">
          <div className="bg-item right light">
            {categoryDictionary[input.category.name]}
          </div>
        </div>
        <div className="col center">
          <div className="bg-item reed right">
            {FormatNumber(input.quantityAmountRemaining)}
          </div>
        </div>
        <div className="col left">
          <div className="bg-item bg-transparent">{input.quantityUnit.name}</div>
        </div>
        <div className="col right">
          <div className="bg-item overflowMinWidth right">
            <InfoTooltip
              tooltip={FormatNumberWithUnitOrNull(input.totalRemainingValue, 'rsd')
                ?? undefined}
              classesProp="bg-text-overflow">
              {FormatNumberWithUnitOrNull(input.totalRemainingValue, 'rsd')}
            </InfoTooltip>
          </div>
        </div>
        <div className="col right">
          <div className="bg-item right">
            {FormatNumber(input.quantityAmountTotal)}
          </div>
        </div>
        <div className="col right"
          onClick={hasWriteAccessTo(user, CONSUMABLES_MODULE_NAME)
            ? (e) => {
              dispatch(
                setUpdateInputItemPopover({
                  isOpen: true,
                  left: e.clientX,
                  top: e.clientY,
                  itemRowIndex: index,
                  focus: "pricePerUnit",
                })
              )
            }
            : undefined}>
          <div className={`bg-item right overflowMinWidth 
            ${hasWriteAccessTo(user, CONSUMABLES_MODULE_NAME) ? "hoverBorder" : ""}
            ${pricePerUnitNotSet ? "bg-danger" : ""}`}>
            <InfoTooltip classesProp="bg-text-overflow"
              tooltip={`${FormatNumberWithUnitOrNull(input.pricePerUnit, "rsd")}`}>
              {FormatNumberWithUnitOrNull(input.pricePerUnit, "rsd")}
            </InfoTooltip>
          </div>
        </div>
        <div className="col right">
          <div className="bg-item right overflowMinWidth">
            <InfoTooltip
              tooltip={FormatNumberWithUnitOrNull(input.totalInitialValue, "rsd")
                ?? undefined}
              classesProp="bg-text-overflow">
              {FormatNumberWithUnitOrNull(input.totalInitialValue, "rsd")}
            </InfoTooltip>
          </div>
        </div>
        <div className="col center">
          <div className="bg-item light center">
            {FormatDateShort(input.date)}
          </div>
        </div>
        <div className="col center">
          {false && hasFullAccessTo(user, CONSUMABLES_MODULE_NAME) // hide delete button since it executes unsafe code
            ? (
              <span
                className="cancel-button"
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();
                  dispatch(
                    setDeleteInputModalOpen({
                      visible: true,
                      index: index,
                    })
                  );
                }}
              >
                <CancelIcon sx={{ color: grey[400] }} fontSize="small" />
              </span>
            )
            : (<></>)}
        </div>
        <div className="col">
          {hasWriteAccessTo(user, CONSUMABLES_MODULE_NAME)
            ? (
              <input className="bg-item bg-transparent" type="text"
                defaultValue={input.note}
                onChange={(e) => {
                  onChangeBasicField({ inputId: input.inputId, note: e.target.value })
                }} />
            )
            : (
              <span className="bg-item bg-transparent">{input.note}</span>
            )}
        </div>
      </div >
    </>
  );
};

export default ConsumablesInput;
